import { gql } from "@apollo/client";
import { FPSO } from "Models/FPSO";
import { FPSOArea } from "Models/FPSOArea";
import { FpsoAreaSystemComponent } from "Models/FpsoAreaSystemComponent";
import {Block} from "../Models/Block";

export const GET_FPSO_AND_AREAS_BY_ID = gql`
  query getFpsoById($id: uniqueidentifier!) {
    fpso: fpsos_by_pk(id: $id) {
      id
      name
      englishTranslation: fpso_description_translations(
        where: { languageCode: { _eq: "en" } }
      ) {
        id
        fpsoDescription
      }
      portugueseTranslation: fpso_description_translations(
        where: { languageCode: { _eq: "pt" } }
      ) {
        id
        fpsoDescription
      }
      latitude
      longitude
      startOfOperations
      fpsoMainContactName
      fpsoMainContactEmail
      block {
        id
        name
        owner {
          companyId
          companyName
          employees: users(where: { stillEmployed: { _eq: true } }) {
            fullName
            id
            userContactInfo {
              email
              phoneNo
            }
          }
        }
        anpgMainContactName
        anpgMainContactEmail
      }

      fpsoAreas: fpso_areas {
        id
        mainContactEmail
        mainContactName
        updatedAt
        createdAt
        currentAuditId
        areaName

        englishTranslation: fpso_area_description_translations(
          where: { languageCode: { _eq: "en" } }
        ) {
          createdAt
          fpsoAreaDescription
          fpsoAreaId
          id
          languageCode
          updatedAt
        }

        portugueseTranslation: fpso_area_description_translations(
          where: { languageCode: { _eq: "pt" } }
        ) {
          createdAt
          fpsoAreaDescription
          fpsoAreaId
          id
          languageCode
          updatedAt
        }

        fpsoAreaPictures: fpso_area_pictures {
          id
          pictureUrl
          thumbUrl
          pictureDescription
          createdAt
          itemsWithSubSystems: items_with_sub_systems {
            createdAt
            id
            itemId
            subSystemType
            updatedAt
          }
        }
        allDocuments: fpso_area_documents {
          id
          type
          name
          description
          reference
          publishDate
          systemArea
          itemsWithSubSystems: items_with_sub_systems {
            createdAt
            id
            itemId
            subSystemType
            updatedAt
          }
        }
      }
    }
  }
`;

export interface GetFpsoAndAreasByIdQueryResult {
  fpso: FPSO;
}

export interface GetFpsoAndAreasByIdQueryVariables {
  id: string;
}

export const GET_FPSO_AREA_BY_ID = gql`
  query getFpsoAreaById($id: uniqueidentifier!) {
    fpsoArea: fpso_areas_by_pk(id: $id) {
      id
      areaName
      updatedAt
      createdAt
      currentAuditId
      mainContactName
      mainContactEmail
      fpso {
        id
        createdAt
        fpsoMainContactEmail
        fpsoMainContactName
        latitude
        longitude
        name
        startOfOperations
        updatedAt
        englishTranslation: fpso_description_translations(
          where: { languageCode: { _eq: "en" } }
        ) {
          createdAt
          fpsoDescription
          fpsoId
          id
          languageCode
          updatedAt
        }

        portugueseTranslation: fpso_description_translations(
          where: { languageCode: { _eq: "pt" } }
        ) {
          createdAt
          fpsoDescription
          fpsoId
          id
          languageCode
          updatedAt
        }
        block {
          id
          name
          owner {
            companyId
            companyName
            employees: users(where: { stillEmployed: { _eq: true } }) {
              fullName
              id
              userContactInfo {
                email
                phoneNo
              }
            }
          }
          anpgMainContactName
          anpgMainContactEmail
        }
      }
      englishTranslation: fpso_area_description_translations(
        where: { languageCode: { _eq: "en" } }
      ) {
        createdAt
        fpsoAreaDescription
        fpsoAreaId
        id
        languageCode
        updatedAt
      }

      portugueseTranslation: fpso_area_description_translations(
        where: { languageCode: { _eq: "pt" } }
      ) {
        createdAt
        fpsoAreaDescription
        fpsoAreaId
        id
        languageCode
        updatedAt
      }

      fpsoAreaPictures: fpso_area_pictures {
        id
        pictureUrl
        thumbUrl
        pictureDescription
        createdAt
        itemsWithSubSystems: items_with_sub_systems {
          createdAt
          id
          itemId
          subSystemType
          updatedAt
        }
      }
      allDocuments: fpso_area_documents {
        id
        type
        name
        description
        reference
        publishDate
        systemArea
        itemsWithSubSystems: items_with_sub_systems {
          createdAt
          id
          itemId
          subSystemType
          updatedAt
        }
      }
    }
  }
`;

export interface GetFpsoAreaByIdQueryResult {
  fpsoArea: FPSOArea;
}

export interface GetFpsoAreaByIdQueryVariables {
  id: string;
}

export const GET_FPSO_AREA_DOCUMENTS_BY_ID = gql`
  query getFpsoAreaDocumentsById(
    $id: uniqueidentifier!
    $whereVariables: fpso_area_documents_bool_exp
  ) {
    fpsoArea: fpso_areas_by_pk(id: $id) {
      id
      areaName
      fpso {
        id
        name
      }
      fpsoAreaDocuments: fpso_area_documents(where: $whereVariables) {
        id
        type
        name
        documentNumber
        revision
        description
        reference
        publishDate
        systemArea
        comment
        uploader {
          id
          fullName
        }
        itemsWithSubSystems: items_with_sub_systems {
          createdAt
          id
          itemId
          subSystemType
          updatedAt
        }
      }
    }
  }
`;

export interface GetFpsoAreaDocumentsByIdQueryResult {
  fpsoArea: FPSOArea;
}

export interface GetFpsoAreaDocumentsByIdQueryVariables {
  id: string;
  whereVariables: object;
}

export const UPDATE_FPSO = gql`
  mutation updateFpso(
    $id: uniqueidentifier!
    $latitude: String
    $longitude: String
    $name: String
    $startOfOperations: date
    $anpgContact: String
    $blockId: uniqueidentifier!
    $anpgMainContactName: String
    $fpsoMainContactName: String
    $anpgMainContactEmail: String
    $fpsoMainContactEmail: String
    $fpsoEnglishDescription: String
    $fpsoPortugueseDescription: String
  ) {
    update_fpsos(
      where: { id: { _eq: $id } }
      _set: {
        latitude: $latitude
        longitude: $longitude
        name: $name
        startOfOperations: $startOfOperations
        fpsoMainContactName: $fpsoMainContactName
        fpsoMainContactEmail: $fpsoMainContactEmail
      }
    ) {
      returning {
        id
      }
    }
    update_blocks(
      where: { id: { _eq: $blockId } }
      _set: {
        anpgMainContactName: $anpgMainContactName
        anpgMainContactEmail: $anpgMainContactEmail
      }
    ) {
      returning {
        id
      }
    }
    updateEnglishDescription: update_fpso_description_translations(
      where: { fpsoId: { _eq: $id }, languageCode: { _eq: "en" } }
      _set: { fpsoDescription: $fpsoEnglishDescription }
    ) {
      returning {
        id
      }
    }
    updatePortugueseDescription: update_fpso_description_translations(
      where: { fpsoId: { _eq: $id }, languageCode: { _eq: "pt" } }
      _set: { fpsoDescription: $fpsoPortugueseDescription }
    ) {
      returning {
        id
      }
    }
  }
`;

export const UPDATE_FPSO_AREA = gql`
  mutation updateFpsoArea(
    $id: uniqueidentifier!
    $areaName: String
    $mainContactEmail: String
    $mainContactName: String
    $fpsoAreaEnglishDescription: String
    $fpsoAreaPortugueseDescription: String
  ) {
    update_fpso_areas(
      where: { id: { _eq: $id } }
      _set: {
        mainContactEmail: $mainContactEmail
        mainContactName: $mainContactName
      }
    ) {
      returning {
        id
      }
    }
    updateEnglishDescription: update_fpso_area_description_translations(
      where: { fpsoAreaId: { _eq: $id }, languageCode: { _eq: "en" } }
      _set: { fpsoAreaDescription: $fpsoAreaEnglishDescription }
    ) {
      returning {
        id
      }
    }
    updatePortugueseDescription: update_fpso_area_description_translations(
      where: { fpsoAreaId: { _eq: $id }, languageCode: { _eq: "pt" } }
      _set: { fpsoAreaDescription: $fpsoAreaPortugueseDescription }
    ) {
      returning {
        id
      }
    }
  }
`;

export const INSERT_FPSO_AREA_SYSTEM_COMPONENT = gql`
  mutation insertFpsoAreaSystemComponent(
    $id: uniqueidentifier!
    $fpsoAreaId: uniqueidentifier!
    $componentName: String
    $manufacturer: String
    $modelNo: String
    $quantity: String
    $type: String
    $serialNo: String
    $description: String
    $comment: String
    $systemArea: String
  ) {
    result: insert_fpso_area_system_components(
      objects: {
        id: $id
        fpsoAreaId: $fpsoAreaId
        componentName: $componentName
        manufacturer: $manufacturer
        modelNo: $modelNo
        quantity: $quantity
        type: $type
        serialNo: $serialNo
        description: $description
        comment: $comment
        systemArea: $systemArea
      }
    ) {
      returning {
        id
      }
    }
  }
`;

export const UPDATE_FPSO_AREA_SYSTEM_COMPONENT = gql`
  mutation updateFpsoAreaSystemComponent(
    $id: uniqueidentifier!
    $systemArea: String
    $componentName: String
    $quantity: String
    $type: String
    $manufacturer: String
    $modelNo: String
    $serialNo: String
    $description: String
    $comment: String
  ) {
    update_fpso_area_system_components(
      where: { id: { _eq: $id } }
      _set: {
        systemArea: $systemArea
        componentName: $componentName
        quantity: $quantity
        type: $type
        manufacturer: $manufacturer
        modelNo: $modelNo
        serialNo: $serialNo
        description: $description
        comment: $comment
      }
    ) {
      returning {
        id
      }
    }
  }
`;

export const DELETE_FPSO_AREA_SYSTEM_COMPONENT = gql`
  mutation deleteFpsoAreaSystemComponent($id: uniqueidentifier!) {
    result: delete_fpso_area_system_components(where: { id: { _eq: $id } }) {
      affected_rows
    }
  }
`;

export const INSERT_FPSO_AREA_DOCUMENT = gql`
  mutation insertFpsoAreaDocument(
    $type: String
    $name: String
    $documentNumber: String
    $revision: String
    $description: String
    $reference: String
    $systemArea: String
    $fpsoAreaId: uniqueidentifier!
    $uploaderId: uniqueidentifier!
    $publishDate: date
  ) {
    result: insert_fpso_area_documents(
      objects: {
        type: $type
        name: $name
        documentNumber: $documentNumber
        revision: $revision
        description: $description
        reference: $reference
        systemArea: $systemArea
        fpsoAreaId: $fpsoAreaId
        uploaderId: $uploaderId
        publishDate: $publishDate
      }
    ) {
      returning {
        id
      }
    }
  }
`;

export const DELETE_FPSO_AREA_DOCUMENT = gql`
  mutation deleteFpsoAreaDocument($id: uniqueidentifier!) {
    result: delete_fpso_area_documents(where: { id: { _eq: $id } }) {
      affected_rows
    }
  }
`;

export const UPDATE_FPSO_AREA_DOCUMENT = gql`
  mutation updateFpsoAreaDocument(
    $id: uniqueidentifier!
    $comment: String
    $description: String
    $documentNumber: String
    $name: String
    $publishDate: date
    $reference: String
    $revision: String
    $systemArea: String
    $type: String
  ) {
    update_fpso_area_documents(
      where: { id: { _eq: $id } }
      _set: {
        comment: $comment
        description: $description
        documentNumber: $documentNumber
        name: $name
        publishDate: $publishDate
        reference: $reference
        revision: $revision
        systemArea: $systemArea
        type: $type
      }
    ) {
      returning {
        id
      }
    }
  }
`;

export const GET_ALL_FPSOS_SCORE_OVER_TIME = gql`
  query getAllFpsosScoreOverTime {
    fpsos(order_by: { block: { name: asc } }) {
      id
      name
      blockId
      fpsoAreas: fpso_areas {
        id
        updatedAt
        createdAt
        currentAuditId
        areaName
        currentAudit: current_audit {
          auditScore
          createdAt
          auditReference
        }
        auditScoresOverTime: audit_snapshots(order_by: { createdAt: asc }) {
          auditScore
          createdAt
          auditReference
        }
      }
    }
    blocks {
      id
      name
      owner {
        companyName
      }
    }
  }
`;

export interface GetAllFpsosScoreOverTimeResult {
  fpsos: FPSO[];
  blocks: Block[];
}

export const GET_FPSO_LEAGUE_TABLE_DATA = gql`
  query getFpsoLeagueTableData {
    fpsos {
      id
      name
      block {
        owner {
          companyName
        }
      }
      fpsoAreas: fpso_areas(
        order_by: { current_audit: { auditScore: desc } }
        where: { currentAuditId: { _is_null: false } }
      ) {
        id
        updatedAt
        createdAt
        currentAuditId
        areaName
        fpso {
          id
          name
          block {
            owner {
              companyName
            }
          }
        }
      }
    }
    fpsoAreas: fpso_areas(
        where: { currentAuditId: { _is_null: false } }
    ) {
      id
      currentAuditCarClassesCount: current_audit {
      auditScore
      startDate
      countComments: cars_aggregate(
        where: {
            isDeleted: { _eq: false }
            status: { _neq: "CLOSED" }
            class: { _eq: "Comment" }
          }
        ) {
          aggregate {
            count
          }
        }
        countMinors: cars_aggregate(
          where: {
            isDeleted: { _eq: false }
            status: { _neq: "CLOSED" }
            class: { _eq: "Minor" }
          }
        ) {
          aggregate {
            count
          }
        }
        countMajors: cars_aggregate(
          where: {
            isDeleted: { _eq: false }
            status: { _neq: "CLOSED" }
            class: { _eq: "Major" }
          }
        ) {
          aggregate {
            count
          }
        }
        countCriticals: cars_aggregate(
          where: {
            isDeleted: { _eq: false }
            status: { _neq: "CLOSED" }
            class: { _eq: "Critical" }
          }
        ) {
          aggregate {
            count
          }
        }
      }
   }
  }`;

export interface GetFpsoLeagueTableDataResult {
  fpsos: FPSO[];
  fpsoAreas: FPSOArea[];
}

export const INSERT_FPSO_AREA_PICTURE = gql`
  mutation insertFpsoAreaPicture(
    $fpsoAreaId: uniqueidentifier!
    $pictureUrl: String
    $thumbUrl: String
    $pictureDescription: String
  ) {
    result: insert_fpso_area_pictures(
      objects: {
        fpsoAreaId: $fpsoAreaId
        pictureUrl: $pictureUrl
        thumbUrl: $thumbUrl
        pictureDescription: $pictureDescription
      }
    ) {
      returning {
        id
      }
    }
  }
`;

export const DELETE_FPSO_AREA_PICTURE = gql`
  mutation deleteFpsoAreaPicture($id: uniqueidentifier!) {
    result: delete_fpso_area_pictures(where: { id: { _eq: $id } }) {
      affected_rows
    }
  }
`;

export const GET_SYSTEM_COMPONENTS_BY_FPSO_AREA = gql`
  query getSystemComponentsByFpsoArea(
    # $fpsoAreaId: uniqueidentifier!
    $whereVariables: fpso_area_system_components_bool_exp
  ) {
    fpsoAreaSystemComponents: fpso_area_system_components(
      where: $whereVariables
      order_by: { createdAt: asc }
    ) {
      comment
      componentName
      createdAt
      description
      fpsoAreaId
      id
      manufacturer
      modelNo
      quantity
      serialNo
      systemArea
      type
      updatedAt
      itemsWithSubSystems: items_with_sub_systems {
        createdAt
        id
        itemId
        subSystemType
        updatedAt
      }
    }
  }
`;

export interface GetSystemComponentsByFpsoAreaQueryResult {
  fpsoAreaSystemComponents: FpsoAreaSystemComponent[];
}

export interface GetSystemComponentsByFpsoAreaQueryVariables {
  // fpsoAreaId: string;
  whereVariables: object;
}

export const DELETE_SUB_SYSTEM_ITEMS_BY_ITEM_ID = gql`
  mutation deleteSubSystemItemsByItemId($itemId: uniqueidentifier!) {
    delete_items_with_sub_systems(where: { itemId: { _eq: $itemId } }) {
      affected_rows
    }
  }
`;

export const INSERT_SUB_SYSTEM_ITEM = gql`
  mutation addSubSystemItem(
    $itemId: uniqueidentifier!
    $subSystemType: String!
  ) {
    insert_items_with_sub_systems(
      objects: { itemId: $itemId, subSystemType: $subSystemType }
    ) {
      returning {
        id
        subSystemType
      }
    }
  }
`;
